import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tabs,
  Tab,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as ApproveIcon,
  PauseCircle as HoldIcon,
  PlayCircle as ReleaseIcon,
  FileDownload as DownloadIcon,
  Send as GenerateIcon,
} from '@mui/icons-material';
import { getToken } from 'src/auth';
import { setBreadcrumbs } from 'src/ui';
import {
  fetchMerchantPayouts,
  fetchISOPayouts,
  fetchPayoutBatches,
  approveMerchantPayouts,
  holdMerchantPayouts,
  releaseMerchantPayouts,
  generateMerchantBatch,
  approveISOPayouts,
  holdISOPayouts,
  generateISOBatch,
  markBatchSubmitted,
  markBatchConfirmed,
  downloadNACHAFile,
} from '../services/payoutsApi';

const STATUS_COLORS = {
  pending: 'warning',
  approved: 'info',
  on_hold: 'default',
  missing_bank_info: 'error',
  processing: 'secondary',
  completed: 'success',
  failed: 'error',
  generated: 'warning',
  submitted: 'info',
  confirmed: 'success',
};

const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  on_hold: 'On Hold',
  missing_bank_info: 'Missing Bank Info',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  generated: 'Generated',
  submitted: 'Submitted',
  confirmed: 'Confirmed',
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function PayoutsPageComponent({ token, setBreadcrumbs }) {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Merchant payouts state
  const [merchantPayouts, setMerchantPayouts] = useState([]);
  const [merchantStatusFilter, setMerchantStatusFilter] = useState('pending');
  const [merchantSearch, setMerchantSearch] = useState('');
  const [merchantSelected, setMerchantSelected] = useState([]);

  // ISO payouts state
  const [isoPayouts, setIsoPayouts] = useState([]);
  const [isoStatusFilter, setIsoStatusFilter] = useState('pending');
  const [isoSearch, setIsoSearch] = useState('');
  const [isoSelected, setIsoSelected] = useState([]);

  // Batches state
  const [batches, setBatches] = useState([]);
  const [batchStatusFilter, setBatchStatusFilter] = useState('all');
  const [batchTypeFilter, setBatchTypeFilter] = useState('all');

  // Hold dialog
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [holdType, setHoldType] = useState('merchant');

  useEffect(() => {
    setBreadcrumbs([
      { label: 'SolventPay', href: '/solventpay' },
      { label: 'Payouts', href: '/solventpay/payouts' },
    ]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch merchant payouts
  const fetchMerchants = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (merchantStatusFilter !== 'all') params.status = merchantStatusFilter;
      const data = await fetchMerchantPayouts(params, token.accessToken);
      setMerchantPayouts(data.results || data);
      setMerchantSelected([]);
    } catch (err) {
      setError(err.message || 'Failed to load merchant payouts');
    } finally {
      setLoading(false);
    }
  }, [token, merchantStatusFilter]);

  // Fetch ISO payouts
  const fetchISOs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (isoStatusFilter !== 'all') params.status = isoStatusFilter;
      const data = await fetchISOPayouts(params, token.accessToken);
      setIsoPayouts(data.results || data);
      setIsoSelected([]);
    } catch (err) {
      setError(err.message || 'Failed to load ISO payouts');
    } finally {
      setLoading(false);
    }
  }, [token, isoStatusFilter]);

  // Fetch batches
  const fetchBatchesData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (batchStatusFilter !== 'all') params.status = batchStatusFilter;
      if (batchTypeFilter !== 'all') params.batch_type = batchTypeFilter;
      const data = await fetchPayoutBatches(params, token.accessToken);
      setBatches(data.results || data);
    } catch (err) {
      setError(err.message || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  }, [token, batchStatusFilter, batchTypeFilter]);

  useEffect(() => {
    if (activeTab === 0) fetchMerchants();
    else if (activeTab === 1) fetchISOs();
    else if (activeTab === 2) fetchBatchesData();
  }, [activeTab, fetchMerchants, fetchISOs, fetchBatchesData]);

  // Handle merchant selection
  const handleMerchantSelectAll = (event) => {
    if (event.target.checked) {
      setMerchantSelected(filteredMerchantPayouts.map((p) => p.id));
    } else {
      setMerchantSelected([]);
    }
  };

  const handleMerchantSelect = (id) => {
    setMerchantSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Handle ISO selection
  const handleIsoSelectAll = (event) => {
    if (event.target.checked) {
      setIsoSelected(filteredIsoPayouts.map((p) => p.id));
    } else {
      setIsoSelected([]);
    }
  };

  const handleIsoSelect = (id) => {
    setIsoSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Merchant actions
  const handleApproveMerchants = async () => {
    if (merchantSelected.length === 0) return;
    try {
      setLoading(true);
      await approveMerchantPayouts(merchantSelected, token.accessToken);
      setSuccessMessage(`Approved ${merchantSelected.length} payouts`);
      fetchMerchants();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleHoldMerchants = () => {
    if (merchantSelected.length === 0) return;
    setHoldType('merchant');
    setHoldDialogOpen(true);
  };

  const handleReleaseMerchants = async () => {
    if (merchantSelected.length === 0) return;
    try {
      setLoading(true);
      await releaseMerchantPayouts(merchantSelected, token.accessToken);
      setSuccessMessage(`Released ${merchantSelected.length} payouts`);
      fetchMerchants();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMerchantBatch = async () => {
    if (merchantSelected.length === 0) return;
    try {
      setLoading(true);
      const result = await generateMerchantBatch(merchantSelected, token.accessToken);
      setSuccessMessage(`Generated batch ${result.file_reference}`);
      fetchMerchants();
      fetchBatchesData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ISO actions
  const handleApproveISOs = async () => {
    if (isoSelected.length === 0) return;
    try {
      setLoading(true);
      await approveISOPayouts(isoSelected, token.accessToken);
      setSuccessMessage(`Approved ${isoSelected.length} ISO payouts`);
      fetchISOs();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleHoldISOs = () => {
    if (isoSelected.length === 0) return;
    setHoldType('iso');
    setHoldDialogOpen(true);
  };

  const handleGenerateISOBatch = async () => {
    if (isoSelected.length === 0) return;
    try {
      setLoading(true);
      const result = await generateISOBatch(isoSelected, token.accessToken);
      setSuccessMessage(`Generated batch ${result.file_reference}`);
      fetchISOs();
      fetchBatchesData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Hold dialog submit
  const handleHoldSubmit = async () => {
    try {
      setLoading(true);
      if (holdType === 'merchant') {
        await holdMerchantPayouts(merchantSelected, holdReason, token.accessToken);
        setSuccessMessage(`Put ${merchantSelected.length} payouts on hold`);
        fetchMerchants();
      } else {
        await holdISOPayouts(isoSelected, holdReason, token.accessToken);
        setSuccessMessage(`Put ${isoSelected.length} ISO payouts on hold`);
        fetchISOs();
      }
      setHoldDialogOpen(false);
      setHoldReason('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Batch actions
  const handleMarkSubmitted = async (batchId) => {
    try {
      setLoading(true);
      await markBatchSubmitted(batchId, token.accessToken);
      setSuccessMessage('Batch marked as submitted');
      fetchBatchesData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkConfirmed = async (batchId) => {
    try {
      setLoading(true);
      await markBatchConfirmed(batchId, token.accessToken);
      setSuccessMessage('Batch confirmed - payouts marked as completed');
      fetchBatchesData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadNACHA = async (batchId, fileReference) => {
    try {
      const content = await downloadNACHAFile(batchId, token.accessToken);
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileReference}.ach`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  // Filter payouts by search
  const filteredMerchantPayouts = merchantPayouts.filter((p) => {
    const search = merchantSearch.toLowerCase();
    return (
      p.merchant_name?.toLowerCase().includes(search) ||
      p.id?.toLowerCase().includes(search)
    );
  });

  const filteredIsoPayouts = isoPayouts.filter((p) => {
    const search = isoSearch.toLowerCase();
    return (
      p.iso_name?.toLowerCase().includes(search) ||
      p.id?.toLowerCase().includes(search)
    );
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Payouts</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Merchant Payouts" />
          <Tab label="ISO Payouts" />
          <Tab label="Batches" />
        </Tabs>
      </Paper>

      {/* Merchant Payouts Tab */}
      <TabPanel value={activeTab} index={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={merchantStatusFilter}
                onChange={(e) => setMerchantStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search..."
              value={merchantSearch}
              onChange={(e) => setMerchantSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          </Box>
          <Box display="flex" gap={1}>
            {merchantSelected.length > 0 && (
              <>
                {merchantStatusFilter === 'pending' && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ApproveIcon />}
                    onClick={handleApproveMerchants}
                    disabled={loading}
                  >
                    Approve ({merchantSelected.length})
                  </Button>
                )}
                {(merchantStatusFilter === 'pending' || merchantStatusFilter === 'approved') && (
                  <Button
                    variant="outlined"
                    startIcon={<HoldIcon />}
                    onClick={handleHoldMerchants}
                    disabled={loading}
                  >
                    Hold
                  </Button>
                )}
                {merchantStatusFilter === 'on_hold' && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ReleaseIcon />}
                    onClick={handleReleaseMerchants}
                    disabled={loading}
                  >
                    Release
                  </Button>
                )}
                {merchantStatusFilter === 'approved' && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<GenerateIcon />}
                    onClick={handleGenerateMerchantBatch}
                    disabled={loading}
                  >
                    Generate Batch
                  </Button>
                )}
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchMerchants}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      merchantSelected.length > 0 &&
                      merchantSelected.length < filteredMerchantPayouts.length
                    }
                    checked={
                      filteredMerchantPayouts.length > 0 &&
                      merchantSelected.length === filteredMerchantPayouts.length
                    }
                    onChange={handleMerchantSelectAll}
                  />
                </TableCell>
                <TableCell>Merchant</TableCell>
                <TableCell>Payout Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Settlements</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Batch</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && merchantPayouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredMerchantPayouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No payouts found
                  </TableCell>
                </TableRow>
              ) : (
                filteredMerchantPayouts.map((payout) => (
                  <TableRow key={payout.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={merchantSelected.includes(payout.id)}
                        onChange={() => handleMerchantSelect(payout.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {payout.merchant_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(payout.payout_date)}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(payout.net_amount_usd)}
                    </TableCell>
                    <TableCell align="center">{payout.settlement_count}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[payout.status] || payout.status}
                        color={STATUS_COLORS[payout.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {payout.batch_file_reference || '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* ISO Payouts Tab */}
      <TabPanel value={activeTab} index={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={isoStatusFilter}
                onChange={(e) => setIsoStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search..."
              value={isoSearch}
              onChange={(e) => setIsoSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          </Box>
          <Box display="flex" gap={1}>
            {isoSelected.length > 0 && (
              <>
                {isoStatusFilter === 'pending' && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ApproveIcon />}
                    onClick={handleApproveISOs}
                    disabled={loading}
                  >
                    Approve ({isoSelected.length})
                  </Button>
                )}
                {(isoStatusFilter === 'pending' || isoStatusFilter === 'approved') && (
                  <Button
                    variant="outlined"
                    startIcon={<HoldIcon />}
                    onClick={handleHoldISOs}
                    disabled={loading}
                  >
                    Hold
                  </Button>
                )}
                {isoStatusFilter === 'approved' && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<GenerateIcon />}
                    onClick={handleGenerateISOBatch}
                    disabled={loading}
                  >
                    Generate Batch
                  </Button>
                )}
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchISOs}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      isoSelected.length > 0 &&
                      isoSelected.length < filteredIsoPayouts.length
                    }
                    checked={
                      filteredIsoPayouts.length > 0 &&
                      isoSelected.length === filteredIsoPayouts.length
                    }
                    onChange={handleIsoSelectAll}
                  />
                </TableCell>
                <TableCell>ISO</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Transactions</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Batch</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && isoPayouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredIsoPayouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No ISO payouts found
                  </TableCell>
                </TableRow>
              ) : (
                filteredIsoPayouts.map((payout) => (
                  <TableRow key={payout.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isoSelected.includes(payout.id)}
                        onChange={() => handleIsoSelect(payout.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {payout.iso_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatDate(payout.period_start)} - {formatDate(payout.period_end)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(payout.net_amount_usd)}
                    </TableCell>
                    <TableCell align="center">{payout.transaction_count}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[payout.status] || payout.status}
                        color={STATUS_COLORS[payout.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {payout.batch_file_reference || '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Batches Tab */}
      <TabPanel value={activeTab} index={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={batchStatusFilter}
                onChange={(e) => setBatchStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="generated">Generated</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={batchTypeFilter}
                onChange={(e) => setBatchTypeFilter(e.target.value)}
                label="Type"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="merchant">Merchant</MenuItem>
                <MenuItem value="iso">ISO</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchBatchesData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File Reference</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Total Amount</TableCell>
                <TableCell align="center">Entries</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && batches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : batches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No batches found
                  </TableCell>
                </TableRow>
              ) : (
                batches.map((batch) => (
                  <TableRow key={batch.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {batch.file_reference}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={batch.batch_type === 'merchant' ? 'Merchant' : 'ISO'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(batch.batch_date)}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(batch.total_amount_usd)}
                    </TableCell>
                    <TableCell align="center">{batch.entry_count}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[batch.status] || batch.status}
                        color={STATUS_COLORS[batch.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadNACHA(batch.id, batch.file_reference)}
                        >
                          Download
                        </Button>
                        {batch.status === 'generated' && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleMarkSubmitted(batch.id)}
                            disabled={loading}
                          >
                            Mark Submitted
                          </Button>
                        )}
                        {batch.status === 'submitted' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleMarkConfirmed(batch.id)}
                            disabled={loading}
                          >
                            Confirm
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Hold Dialog */}
      <Dialog open={holdDialogOpen} onClose={() => setHoldDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Put Payouts On Hold</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Hold"
            fullWidth
            multiline
            rows={3}
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHoldDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleHoldSubmit} variant="contained" disabled={loading}>
            Put On Hold
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Box>
  );
}

const mapStateToProps = (state) => ({
  token: getToken(state),
});

const mapDispatchToProps = {
  setBreadcrumbs,
};

export const PayoutsPage = connect(mapStateToProps, mapDispatchToProps)(PayoutsPageComponent);
